import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ServiceAreaChecker } from "@/components/ServiceAreaChecker";
import { Gallery } from "@/components/Gallery";
import { Reviews } from "@/components/Reviews";
import { HERO_IMG, MOUNTAINS_IMG, COMMUNITIES, PLANS, BRAND, FLYER } from "@/lib/config";
import { ShieldCheck, Leaf, Sparkles, MapPin, Star, Truck, Phone } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

export default function Home() {
  const navigate = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="bg-[#F8FAFC]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-boco-navy text-white">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center relative">
          <div>
            <motion.div initial="hidden" animate="show" variants={fadeUp} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.15em] text-boco-green">
              <MapPin className="h-3.5 w-3.5" /> BASED IN BUCHANAN, VA · SERVING SURROUNDING COMMUNITIES
            </motion.div>
            <motion.h1 initial="hidden" animate="show" custom={1} variants={fadeUp} className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              LOCAL. PROFESSIONAL.
              <span className="text-boco-green"> CLEAN.</span>
            </motion.h1>
            <motion.p initial="hidden" animate="show" custom={2} variants={fadeUp} className="mt-5 text-lg text-slate-300 max-w-xl">
              Professional residential trash-can cleaning that sanitizes and deodorizes your bins right at the curb.
              Serving Buchanan, Botetourt County and surrounding communities.
            </motion.p>
            <motion.div initial="hidden" animate="show" custom={3} variants={fadeUp} className="mt-7 flex flex-wrap gap-4">
              <button onClick={() => navigate("/book")} className="rounded-full bg-boco-green px-7 py-3.5 font-bold text-boco-navy hover:bg-white transition-colors" data-testid="hero-book-btn">
                Book Your Cleaning
              </button>
              <button onClick={() => scrollTo("service-check")} className="rounded-full border border-white/30 px-7 py-3.5 font-bold text-white hover:bg-white/10 transition-colors" data-testid="hero-check-zip-btn">
                Check Your ZIP Code
              </button>
            </motion.div>
            <motion.div initial="hidden" animate="show" custom={4} variants={fadeUp} className="mt-8 flex items-center gap-6 text-sm text-slate-300">
              <a href={BRAND.phoneHref} className="flex items-center gap-2 font-semibold hover:text-boco-green transition-colors"><Phone className="h-4 w-4" /> {BRAND.phone}</a>
              <span className="flex items-center gap-1"><Star className="h-4 w-4 text-boco-green fill-boco-green" /> Family-run & local</span>
            </motion.div>
          </div>

          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/before-after.jpg`} alt="BOCO BINS trash can before and after cleaning" className="rounded-3xl object-cover object-top w-full h-64 lg:h-80 shadow-2xl bg-white" />
            <div id="service-check" className="lg:-mt-16 lg:ml-8 relative z-10 scroll-mt-28">
              <ServiceAreaChecker />
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-white border-b border-slate-100">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-8 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Sparkles, t: "We Clean Inside & Out" },
            { icon: ShieldCheck, t: "Removes Bacteria & Odors" },
            { icon: Leaf, t: "Eco-Friendly Solutions" },
            { icon: Truck, t: "Curbside — No Hassle" },
          ].map((f) => (
            <div key={f.t} className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-boco-greenlight text-boco-greendark">
                <f.icon className="h-5 w-5" />
              </div>
              <span className="font-semibold text-boco-navy text-sm">{f.t}</span>
            </div>
          ))}
        </div>
      </section>

      {/* COMMUNITIES */}
      <section id="communities" className="relative overflow-hidden py-20">
        <img src={MOUNTAINS_IMG} alt="Blue Ridge Mountains, Virginia" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-boco-navy/90" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 text-center text-white">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold tracking-[0.15em] text-boco-green">
            <MapPin className="h-3.5 w-3.5" /> PROUDLY SERVING OUR LOCAL COMMUNITY
          </div>
          <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">
            Clean Cans. Healthier Homes.
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-slate-300 text-lg">
            BOCO BINS LLC is based in Buchanan, Virginia and proudly provides professional residential
            trash-can cleaning throughout Botetourt County and surrounding communities.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {COMMUNITIES.map((c, i) => (
              <motion.span
                key={c}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="rounded-full bg-white/95 px-6 py-3 font-display font-extrabold text-boco-navy tracking-wide uppercase text-sm hover:bg-boco-green transition-colors cursor-default"
                data-testid={`community-badge-${i}`}
              >
                {c}
              </motion.span>
            ))}
          </div>
          <button onClick={() => scrollTo("service-check")} className="mt-10 rounded-full bg-boco-green px-8 py-3.5 font-bold text-boco-navy hover:bg-white transition-colors" data-testid="communities-check-zip-btn">
            Check Your ZIP Code
          </button>
        </div>
      </section>

      {/* PRICING */}
      <Gallery />

      {/* PRICING */}
      <section id="pricing" className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
        <div className="text-center">
          <div className="text-xs font-bold tracking-[0.2em] text-boco-green">CHOOSE THE PLAN THAT WORKS FOR YOU</div>
          <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-black text-boco-navy tracking-tight">
            Simple, Honest Pricing
          </h2>
          <p className="mt-3 text-slate-600">Same great price throughout our entire service area.</p>
        </div>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 items-stretch">
          {PLANS.map((p, i) => (
            <motion.div
              key={p.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className={`relative rounded-3xl border p-8 bg-white flex flex-col ${p.featured ? "border-boco-green shadow-[0_20px_60px_-15px_rgba(108,191,63,0.5)]" : "border-slate-200 shadow-sm"}`}
              data-testid={`pricing-card-${p.key}`}
            >
              {p.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-boco-green px-4 py-1 text-xs font-bold text-boco-navy">
                  MOST POPULAR
                </div>
              )}
              <h3 className="font-display text-xl font-extrabold text-boco-navy uppercase tracking-wide">{p.name}</h3>
              <div className="mt-4 flex items-end gap-1">
                <span className="font-display text-5xl font-black text-boco-navy">${p.price}</span>
                <span className="mb-2 text-slate-500 text-sm">{p.cadence}</span>
              </div>
              <p className="mt-4 text-slate-600 text-sm min-h-[48px]">{p.desc}</p>
              <button
                onClick={() => navigate(`/book?plan=${p.key}`)}
                className={`mt-6 w-full rounded-full px-6 py-3 font-bold transition-colors mt-auto ${p.featured ? "bg-boco-green text-boco-navy hover:bg-boco-greendark hover:text-white" : "bg-boco-navy text-white hover:bg-boco-navylight"}`}
                data-testid={`select-plan-${p.key}`}
              >
                Book {p.name}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <Reviews />

      {/* CTA */}
      <section className="bg-boco-navy">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 text-center text-white">
          <h2 className="font-display text-3xl sm:text-4xl font-black">Schedule Your Cleaning Today!</h2>
          <p className="mt-3 text-slate-300 text-lg">Clean cans, healthier homes. It's what we do.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button onClick={() => navigate("/book")} className="rounded-full bg-boco-green px-8 py-3.5 font-bold text-boco-navy hover:bg-white transition-colors" data-testid="cta-book-btn">
              Book Your Cleaning
            </button>
            <a href={BRAND.phoneHref} className="rounded-full border border-white/30 px-8 py-3.5 font-bold text-white hover:bg-white/10 transition-colors flex items-center gap-2">
              <Phone className="h-4 w-4" /> {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
